/**
 * BusinessUnit describes all of the supported business unit values found in
 * GroupTrip.businessUnit
 */
enum BusinessUnit {
  StudentEast = 'USEAST',
  StudentWest = 'USWEST',
  ProfessionalDevelopment = 'USPD',
  USConventions = 'USCONV',
  StudyAbroad = 'CST', // Formerly CollegeStudyTours
  ToursForGirls = 'GSCOUTS',
  RestOfCanadaInternationalFlight = 'ROCINT',
  QuebecInternationalFlight = 'QCINT',
  CanadaConventions = 'CACONV',
  RestOfCanadaDomesticFlight = 'ROCDOM',
  QuebecDomesticFlight = 'QCDOM',
  Voluntours = 'VOLTOURS',
  RestOfCanadaBus = 'ROCBUS',
  QuebecBus = 'QCBUS',
  ExploreAmericaDomesticFlight = 'EADOMFL',
  ExploreAmericaDomesticBus = 'EADOMBS',
  ExploreAmericaConventions = 'EACONV',
  ExploreAmericaSpecialEvents = 'EASPCL',
  ExploreAmericaStrategicAccounts = 'EASTRAT',
}

/**
 * BusinessUnitGroup (i.e., BUG) describes groupings of business units (i.e., BUs).
 *
 * Note: Former BusinessUnitGroups that are no longer supported:
 * - UltimateBreak (UB/CB/COUS) - UB has their own engineering org (limited support in select apps; e.g., OnTour purchase in trex)
 * - ToursForGirls (GST/TFG) - This is now rolled up into ETUS
 * - NorthAmericaBus (NAB) - This is now rolled up into ETCA
 */
enum BusinessUnitGroup {
  EFTours = 'ETUS',
  ExploreAmerica = 'EAUS',
  EFToursCanada = 'ETCA',
  StudyAbroad = 'CST', // Formerly CollegeStudyTours
}

/**
 * Helper utility that tries to map a string representation of a BusinessUnit (BU), group or other string key
 * to a businessUnitGroup (BUG)
 *
 * Note: Former BusinessUnitGroups that are no longer supported:
 * - UltimateBreak (UB/CB/COUS) - UB has their own engineering org (limited support in select apps; e.g., OnTour purchase in trex)
 * - ToursForGirls (GST/TFG) - This is now rolled up into ETUS
 * - NorthAmericaBus (NAB) - This is now rolled up into ETCA
 *
 * @param key - a business unit or string value of a BUG or former BUG (e.g., GST)
 * @returns Matched BusinessUnitGroup, otherwise `throw`s
 */
const getBusinessUnitGroup = (key: string | BusinessUnit | BusinessUnitGroup) => {
  switch (key.toUpperCase()) {
    case BusinessUnit.StudentEast:
    case BusinessUnit.StudentWest:
    case BusinessUnit.ProfessionalDevelopment:
    case BusinessUnit.USConventions:
    case BusinessUnit.ToursForGirls:
    case BusinessUnitGroup.EFTours:
    case 'GST':
      return BusinessUnitGroup.EFTours;

    case BusinessUnit.RestOfCanadaInternationalFlight:
    case BusinessUnit.QuebecInternationalFlight:
    case BusinessUnit.CanadaConventions:
    case BusinessUnit.RestOfCanadaDomesticFlight:
    case BusinessUnit.QuebecDomesticFlight:
    case BusinessUnit.Voluntours:
    case BusinessUnit.RestOfCanadaBus: // formerly part of NAB
    case BusinessUnit.QuebecBus: // formerly part of NAB
    case BusinessUnitGroup.EFToursCanada:
    case 'ETFC':
    case 'NAB':
      return BusinessUnitGroup.EFToursCanada;

    case BusinessUnit.ExploreAmericaDomesticFlight:
    case BusinessUnit.ExploreAmericaDomesticBus:
    case BusinessUnit.ExploreAmericaConventions:
    case BusinessUnit.ExploreAmericaSpecialEvents:
    case BusinessUnit.ExploreAmericaStrategicAccounts:
    case BusinessUnitGroup.ExploreAmerica:
      return BusinessUnitGroup.ExploreAmerica;

    case BusinessUnit.StudyAbroad:
      return BusinessUnitGroup.StudyAbroad;

    default:
      throw new Error(`Unrecognized BusinessUnit or Group identifier: "${key}", unable to map to a BusinessUnitGroup`);
  }
};

/**
 * Note: This utility function is poorly named. It produces a "product code" to use with the tour content service. It is
 * not meant for using with Touro directly.
 *
 * Returns BusinessUnitGroup or "GST" from the businessUnit,
 *
 * @param businessUnit
 * @deprecated - Once Tour Content Service is fully migrated to ToursApi Graph, there is no reason to use this function. Prefer
 *  migrating to use ToursAPI for tour content.
 */
const getTouroSupportedProduct = (businessUnit: string): BusinessUnitGroup | 'GST' => {
  return businessUnit === BusinessUnit.ToursForGirls ? 'GST' : getBusinessUnitGroup(businessUnit);
};

const isAmericanBusiness = (bug: BusinessUnitGroup) =>
  bug === BusinessUnitGroup.EFTours ||
  bug === BusinessUnitGroup.ExploreAmerica ||
  bug === BusinessUnitGroup.StudyAbroad;

const isCanadianBusiness = (bug: BusinessUnitGroup) => bug === BusinessUnitGroup.EFToursCanada;

export {
  getBusinessUnitGroup,
  BusinessUnit,
  BusinessUnitGroup,
  isAmericanBusiness,
  isCanadianBusiness,
  getTouroSupportedProduct,
};
